// data-fit LLC · DecisionFlow · EXECUTE Stage
// ExecuteFlowShell.jsx · DDL: R5 · API: v1.11 · Session 92
// Auth: useAuth() direct — ARCH-2 compliant
// Key constraints: exports SCREEN_ORDER, owns AI state, CustomEvent navigation only
// Patched: S205 — CSS fix: bare class names replaced with Tailwind utilities (no new CSS files)
// Patched: S206-01 — scenarioSetId + fiscalPeriodLabel added to props and sharedProps
// M040 C2: hardcoded colors → tokens, text-* → text-tok-*

import React, { useEffect, useMemo, useState } from 'react'
import { useAuth } from '../../auth/AuthContext.jsx'
import ReadinessGateDashboard from '../tenant-admin/ReadinessGateDashboard'
import CalculationRunInitiator from '../tenant-admin/CalculationRunInitiator'
import CalculationRunMonitor from '../tenant-admin/CalculationRunMonitor'
import PayoutResultsViewer from '../tenant-admin/PayoutResultsViewer'
import StatementBatchManager from '../tenant-admin/StatementBatchManager'
import PayrollExportManager from '../tenant-admin/PayrollExportManager'

export const SCREEN_ORDER = [
  'execute.readiness_gate',
  'execute.calculation_run_initiator',
  'execute.calculation_run_monitor',
  'execute.payout_results',
  'execute.statement_batch',
  'execute.payroll_export',
]

const SCREEN_LABELS = {
  'execute.readiness_gate':            'Readiness',
  'execute.calculation_run_initiator': 'Run Setup',
  'execute.calculation_run_monitor':   'Run Monitor',
  'execute.payout_results':            'Payouts',
  'execute.statement_batch':           'Statements',
  'execute.payroll_export':            'Payroll Export',
}

export default function ExecuteFlowShell({
  initialScreen = SCREEN_ORDER[0],
  fiscalPeriodId,
  fiscalPeriodLabel,
  scenarioSetId,
  selectedRunId,
  onPreviewRerunComplete,
}) {
  useAuth()

  const normalizedInitialScreen = SCREEN_ORDER.includes(initialScreen) ? initialScreen : SCREEN_ORDER[0]

  const [activeScreen, setActiveScreen] = useState(normalizedInitialScreen)
  const [aiStatus, setAiStatus] = useState('idle')
  const [aiResponse, setAiResponse] = useState(null)
  const [runId, setRunId] = useState(selectedRunId || null)

  useEffect(() => {
    const onNavigate = (event) => {
      const nextScreen = event?.detail?.screen
      if (SCREEN_ORDER.includes(nextScreen)) setActiveScreen(nextScreen)
    }
    window.addEventListener('decisionflow:navigate', onNavigate)
    return () => window.removeEventListener('decisionflow:navigate', onNavigate)
  }, [])

  const navigateToIndex = (index) => {
    const screen = SCREEN_ORDER[index]
    if (!screen) return
    window.dispatchEvent(new CustomEvent('decisionflow:navigate', { detail: { screen } }))
  }

  const activeIndex = SCREEN_ORDER.indexOf(activeScreen)

  const sharedProps = useMemo(
    () => ({ fiscalPeriodId, fiscalPeriodLabel, scenarioSetId, aiStatus, aiResponse, setAiStatus, setAiResponse, runId, setRunId, onPreviewRerunComplete }),
    [fiscalPeriodId, fiscalPeriodLabel, scenarioSetId, aiStatus, aiResponse, runId, onPreviewRerunComplete],
  )

  const renderScreen = () => {
    switch (activeScreen) {
      case SCREEN_ORDER[0]: return <ReadinessGateDashboard {...sharedProps} />
      case SCREEN_ORDER[1]: return <CalculationRunInitiator {...sharedProps} />
      case SCREEN_ORDER[2]: return <CalculationRunMonitor {...sharedProps} />
      case SCREEN_ORDER[3]: return <PayoutResultsViewer {...sharedProps} />
      case SCREEN_ORDER[4]: return <StatementBatchManager {...sharedProps} />
      case SCREEN_ORDER[5]: return <PayrollExportManager {...sharedProps} />
      default:              return <ReadinessGateDashboard {...sharedProps} />
    }
  }

  const btnSecondary = 'rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-4 py-2 text-tok-sm font-medium text-[rgb(var(--color-text))] hover:bg-[rgb(var(--color-surface-muted))] disabled:opacity-40 disabled:cursor-not-allowed'

  return (
    <section className="flex flex-col gap-6 p-6 min-h-full bg-[rgb(var(--color-bg))]">

      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="text-tok-xs font-semibold uppercase tracking-widest text-[rgb(var(--color-text-muted))] mb-1">
            DecisionFlow Execute
          </p>
          <h1 className="text-tok-2xl font-semibold text-[rgb(var(--color-text))]">Execute Stage</h1>
          <p className="mt-1 text-tok-sm text-[rgb(var(--color-text-muted))]">
            Monitor readiness, run calculations, review payouts, release statements, and create payroll exports.
          </p>
        </div>

        <div
          className="flex items-center gap-2 rounded-lg bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] px-3 py-2 text-tok-sm shadow-sm"
          aria-live="polite"
        >
          <span className="text-[rgb(var(--color-text-muted))] font-medium">AI Status</span>
          <strong className="text-[rgb(var(--color-text))]">{aiStatus}</strong>
        </div>
      </header>

      {/* Step nav */}
      <nav className="flex items-center gap-1 flex-wrap" aria-label="Execute stage screens">
        {SCREEN_ORDER.map((screen, index) => {
          const isActive = screen === activeScreen
          const isComplete = index < activeIndex
          return (
            <button
              key={screen}
              type="button"
              className={[
                'flex items-center gap-2 rounded-lg px-3 py-2 text-tok-sm font-medium transition-colors',
                isActive
                  ? 'bg-[rgb(var(--color-primary))] text-white shadow-sm'
                  : isComplete
                  ? 'bg-[rgb(var(--color-success))]/10 text-[rgb(var(--color-success))] hover:bg-[rgb(var(--color-success))]/20'
                  : 'bg-[rgb(var(--color-surface))] border border-[rgb(var(--color-border))] text-[rgb(var(--color-text-muted))] hover:bg-[rgb(var(--color-surface-muted))]'
              ].join(' ')}
              onClick={() => navigateToIndex(index)}
            >
              <span className={[
                'flex h-5 w-5 items-center justify-center rounded-full text-tok-xs font-semibold',
                isActive ? 'bg-white/20 text-white' : isComplete ? 'bg-[rgb(var(--color-success))]/20 text-[rgb(var(--color-success))]' : 'bg-[rgb(var(--color-surface-muted))] text-[rgb(var(--color-text-muted))]'
              ].join(' ')}>
                {index + 1}
              </span>
              <span>{SCREEN_LABELS[screen]}</span>
            </button>
          )
        })}
      </nav>

      {/* Screen content */}
      <main className="flex-1">
        {renderScreen()}
      </main>

      {/* Footer nav */}
      <footer className="flex items-center gap-3 pt-4 border-t border-[rgb(var(--color-border))]">
        <button type="button" className={btnSecondary} disabled={activeIndex <= 0} onClick={() => navigateToIndex(activeIndex - 1)}>Back</button>
        <button type="button" className={btnSecondary} disabled={activeIndex >= SCREEN_ORDER.length - 1} onClick={() => navigateToIndex(activeIndex + 1)}>Next</button>
      </footer>
    </section>
  )
}
